import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { createConstituencySlug } from '../navigation';
import Clickable from './Clickable';
import { constResultGridCols } from '../constants/gridConstants';

const RESULTS_PATH = '/json/indiaElectionResuls2024.json';
const PARTIES_PATH = '/json/party.json';

function compareValues(first, second) {
	const firstText = String(first ?? '').trim();
	const secondText = String(second ?? '').trim();
	const firstNumber = Number(firstText);
	const secondNumber = Number(secondText);

	if (firstText !== '' && secondText !== '' && Number.isFinite(firstNumber) && Number.isFinite(secondNumber)) {
		return firstNumber - secondNumber;
	}

	return firstText.localeCompare(secondText, undefined, { numeric: true, sensitivity: 'base' });
}

function ResultTable({ constituency }) {
	const [records, setRecords] = useState([]);
	const [partyNames, setPartyNames] = useState({});
	const [status, setStatus] = useState('loading');
	const [filterText, setFilterText] = useState('');

	useEffect(() => {
		let cancelled = false;

		fetch(RESULTS_PATH)
			.then((response) => {
				if (!response.ok) {
					throw new Error('Could not load election results');
				}
				return response.json();
			})
			.then((results) => {
				if (!cancelled) {
					setRecords(Array.isArray(results)
						? results.map((record, index) => ({ ...record, __rowId: index }))
						: []);
					setStatus('ready');
				}
			})
			.catch(() => {
				if (!cancelled) {
					setStatus('error');
				}
			});

		fetch(PARTIES_PATH)
			.then((response) => {
				if (!response.ok) {
					throw new Error('Could not load party names');
				}
				return response.json();
			})
			.then((parties) => {
				if (!cancelled && Array.isArray(parties)) {
					setPartyNames(Object.fromEntries(
						parties
							.filter((party) => party.abbreviation && party.name)
							.map((party) => [party.abbreviation, party.name]),
					));
				}
			})
			.catch(() => {
				if (!cancelled) {
					setPartyNames({});
				}
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const rows = constituency
		? records.filter((record) => createConstituencySlug(record.parlConstName) === constituency)
		: records;
	const columns = records.length > 0
		? Object.keys(constResultGridCols)
			.filter((field) => field !== '__rowId')
			.map((field) => ({
				name: constResultGridCols[field],
				selector: (row) => row[field] ?? '',
				...(field === 'partyName' && {
					cell: (row) => <span title={partyNames[row[field]] || undefined}>{row[field] ?? ''}</span>,
				}),
				sortable: true,
				sortFunction: (first, second) => compareValues(first[field], second[field]),
			}))
		: [];
	const normalizedFilter = filterText.trim().toLocaleLowerCase();
	const filteredRows = normalizedFilter
		? rows.filter((row) => Object.entries(row)
			.filter(([field]) => field !== '__rowId')
			.some(([, value]) => String(value ?? '').toLocaleLowerCase().includes(normalizedFilter)))
		: rows;

	return (
		<main className="results-page">
			<div className="results-page__heading">
				<div>
					<p className="results-page__eyebrow">2024 General Election</p>
					<h1>{constituency ? rows[0]?.parlConstName || 'Constituency results' : 'Election results'}</h1>
					{rows[0] && <p className="results-page__state">{rows[0].stateName}</p>}
				</div>
				<Clickable className="results-page__back" to="/">Back to map</Clickable>
			</div>

			{status === 'loading' && <p role="status">Loading results...</p>}
			{status === 'error' && <p role="alert">Election results could not be loaded.</p>}
			{status === 'ready' && rows.length === 0 && (
				<p role="status">No election results were found for this constituency.</p>
			)}
			{status === 'ready' && rows.length > 0 && (
				<div className="results-table-container">
					<label className="results-table-filter">
						<span>Filter results</span>
						<input
							type="search"
							value={filterText}
							onChange={(event) => setFilterText(event.target.value)}
							placeholder="Search any column"
						/>
					</label>
					<DataTable
						columns={columns}
						data={filteredRows}
						keyField="__rowId"
						pagination
						paginationPerPage={25}
						paginationRowsPerPageOptions={[10, 25, 50, 100]}
						noDataComponent={normalizedFilter ? 'No results match your filter.' : 'No election results found.'}
						responsive
					/>
				</div>
			)}
		</main>
	);
}

export default ResultTable;